import React, { useState, useEffect } from 'react';
import './MediaManagement.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MediaManagement({ onClose }) {
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFile, setUploadingFile] = useState('');
  const [previewVideo, setPreviewVideo] = useState(null);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [renamingItem, setRenamingItem] = useState(null);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      const response = await fetch(`${API_URL}/api/media/list`);
      const data = await response.json();
      setVideos(data.videos || []);
      setAudios(data.audios || []);
    } catch (err) {
      setError('Fehler beim Laden der Medien');
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.mp4')) {
      setError('Nur MP4-Dateien erlaubt');
      return;
    }

    if (file.size > 500 * 1024 * 1024) {
      setError('Video zu groß (max 500MB)');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadingFile(file.name);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
        }
      });

      // Handle completion
      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          setSuccess(`Video "${file.name}" erfolgreich hochgeladen!`);
          await loadMedia();
          e.target.value = '';
          setTimeout(() => setSuccess(''), 5000);
        } else {
          const errorData = JSON.parse(xhr.responseText);
          setError(errorData.detail || 'Upload fehlgeschlagen');
        }
        setUploading(false);
        setUploadProgress(0);
        setUploadingFile('');
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        setError('Upload fehlgeschlagen');
        setUploading(false);
        setUploadProgress(0);
        setUploadingFile('');
      });

      xhr.open('POST', `${API_URL}/api/media/upload/video`);
      xhr.send(formData);
    } catch (err) {
      setError('Upload fehlgeschlagen');
      setUploading(false);
      setUploadProgress(0);
      setUploadingFile('');
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isValidAudio = file.name.toLowerCase().endsWith('.mp3') || 
                          file.name.toLowerCase().endsWith('.wav');
    if (!isValidAudio) {
      setError('Nur MP3- und WAV-Dateien erlaubt');
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      setError('Audio zu groß (max 25MB)');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadingFile(file.name);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
        }
      });

      // Handle completion
      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          setSuccess(`Audio "${file.name}" erfolgreich hochgeladen!`);
          await loadMedia();
          e.target.value = '';
          setTimeout(() => setSuccess(''), 5000);
        } else {
          const errorData = JSON.parse(xhr.responseText);
          setError(errorData.detail || 'Upload fehlgeschlagen');
        }
        setUploading(false);
        setUploadProgress(0);
        setUploadingFile('');
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        setError('Upload fehlgeschlagen');
        setUploading(false);
        setUploadProgress(0);
        setUploadingFile('');
      });

      xhr.open('POST', `${API_URL}/api/media/upload/audio`);
      xhr.send(formData);
    } catch (err) {
      setError('Upload fehlgeschlagen');
      setUploading(false);
      setUploadProgress(0);
      setUploadingFile('');
    }
  };

  const handleDeleteVideo = async (filename) => {
    if (!window.confirm(`Video "${filename}" wirklich löschen?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/media/delete/video/${filename}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await loadMedia();
        if (previewVideo === `${API_URL}/api/static/video/${filename}`) {
          setPreviewVideo(null);
        }
      } else {
        setError('Löschen fehlgeschlagen');
      }
    } catch (err) {
      setError('Löschen fehlgeschlagen');
    }
  };

  const handleDeleteAudio = async (filename) => {
    if (!window.confirm(`Audio "${filename}" wirklich löschen?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/media/delete/audio/${filename}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await loadMedia();
        if (previewAudio === `${API_URL}/api/static/audio/${filename}`) {
          setPreviewAudio(null);
        }
      } else {
        setError('Löschen fehlgeschlagen');
      }
    } catch (err) {
      setError('Löschen fehlgeschlagen');
    }
  };

  const handleRenameVideo = async (oldName) => {
    if (!newName.trim() || !newName.endsWith('.mp4')) {
      setError('Name muss mit .mp4 enden');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/media/rename/video?old_name=${oldName}&new_name=${newName}`, {
        method: 'POST',
      });

      if (response.ok) {
        await loadMedia();
        setRenamingItem(null);
        setNewName('');
      } else {
        const error = await response.json();
        setError(error.detail || 'Umbenennen fehlgeschlagen');
      }
    } catch (err) {
      setError('Umbenennen fehlgeschlagen');
    }
  };

  const handleRenameAudio = async (oldName) => {
    if (!newName.trim() || !newName.endsWith('.mp3')) {
      setError('Name muss mit .mp3 enden');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/media/rename/audio?old_name=${oldName}&new_name=${newName}`, {
        method: 'POST',
      });

      if (response.ok) {
        await loadMedia();
        setRenamingItem(null);
        setNewName('');
      } else {
        const error = await response.json();
        setError(error.detail || 'Umbenennen fehlgeschlagen');
      }
    } catch (err) {
      setError('Umbenennen fehlgeschlagen');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredAudios = audios.filter(audio =>
    audio.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="media-management-overlay" data-testid="media-management">
      <div className="media-management-container fade-in">
        <div className="media-management-header">
          <h2>Medien-Verwaltung</h2>
          <button className="close-button" onClick={onClose} data-testid="close-management">
            ✕
          </button>
        </div>

        {error && (
          <div className="error-banner" data-testid="error-message">
            {error}
            <button onClick={() => setError('')} className="close-error">✕</button>
          </div>
        )}

        {success && (
          <div className="success-banner" data-testid="success-message">
            ✓ {success}
            <button onClick={() => setSuccess('')} className="close-error">✕</button>
          </div>
        )}

        {uploading && (
          <div className="upload-progress-container">
            <div className="upload-progress-header">
              <span className="upload-filename">📤 {uploadingFile}</span>
              <span className="upload-percentage">{uploadProgress}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="media-sections">
          {/* Videos Section */}
          <div className="media-section">
            <div className="section-header">
              <h3>🎥 Videos ({videos.length})</h3>
              <label className="upload-button-compact">
                <input
                  type="file"
                  accept=".mp4"
                  onChange={handleVideoUpload}
                  disabled={uploading}
                  data-testid="video-upload-input"
                />
                + Hochladen
              </label>
            </div>

            <div className="media-grid">
              {videos.map((video) => (
                <div key={video.name} className="media-card">
                  {renamingItem === `video-${video.name}` ? (
                    <div className="rename-input-container">
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="neuer-name.mp4"
                        className="rename-input"
                        autoFocus
                      />
                      <div className="rename-actions">
                        <button onClick={() => handleRenameVideo(video.name)} className="btn-save">✓</button>
                        <button onClick={() => { setRenamingItem(null); setNewName(''); }} className="btn-cancel">✕</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="media-card-icon">🎬</div>
                      <div className="media-card-info">
                        <div className="media-card-name" title={video.name}>{video.name}</div>
                        <div className="media-card-size">{formatFileSize(video.size)}</div>
                      </div>
                      <div className="media-card-actions">
                        <button
                          onClick={() => setPreviewVideo(`${API_URL}${video.url}`)}
                          className="action-btn preview"
                          title="Vorschau"
                          data-testid={`preview-video-${video.name}`}
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => {
                            setRenamingItem(`video-${video.name}`);
                            setNewName(video.name);
                          }}
                          className="action-btn rename"
                          title="Umbenennen"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteVideo(video.name)}
                          className="action-btn delete"
                          title="Löschen"
                          data-testid={`delete-video-${video.name}`}
                        >
                          🗑️
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {videos.length === 0 && (
                <div className="empty-state">
                  <div className="empty-icon">📹</div>
                  <p>Keine Videos vorhanden</p>
                  <p className="empty-hint">Laden Sie Ihr erstes Video hoch</p>
                </div>
              )}
            </div>

            {previewVideo && (
              <div className="preview-modal" onClick={() => setPreviewVideo(null)}>
                <div className="preview-content" onClick={(e) => e.stopPropagation()}>
                  <video
                    src={previewVideo}
                    controls
                    autoPlay
                    className="video-preview"
                    data-testid="video-preview"
                  />
                  <button onClick={() => setPreviewVideo(null)} className="close-preview">Schließen</button>
                </div>
              </div>
            )}
          </div>

          {/* Audio Section */}
          <div className="media-section">
            <div className="section-header">
              <h3>🎵 Musik ({audios.length})</h3>
              <label className="upload-button-compact">
                <input
                  type="file"
                  accept=".mp3,.wav"
                  onChange={handleAudioUpload}
                  disabled={uploading}
                  data-testid="audio-upload-input"
                />
                + Hochladen
              </label>
            </div>

            <div className="search-container">
              <input
                type="text"
                placeholder="Musik suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
                data-testid="audio-search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="clear-search">✕</button>
              )}
            </div>

            <div className="media-grid">
              {filteredAudios.map((audio) => (
                <div key={audio.name} className="media-card">
                  {renamingItem === `audio-${audio.name}` ? (
                    <div className="rename-input-container">
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="neuer-name.mp3"
                        className="rename-input"
                        autoFocus
                      />
                      <div className="rename-actions">
                        <button onClick={() => handleRenameAudio(audio.name)} className="btn-save">✓</button>
                        <button onClick={() => { setRenamingItem(null); setNewName(''); }} className="btn-cancel">✕</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="media-card-icon">🎵</div>
                      <div className="media-card-info">
                        <div className="media-card-name" title={audio.name}>{audio.name}</div>
                        <div className="media-card-size">{formatFileSize(audio.size)}</div>
                      </div>
                      <div className="media-card-actions">
                        <button
                          onClick={() => setPreviewAudio(`${API_URL}${audio.url}`)}
                          className="action-btn preview"
                          title="Vorschau"
                          data-testid={`preview-audio-${audio.name}`}
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => {
                            setRenamingItem(`audio-${audio.name}`);
                            setNewName(audio.name);
                          }}
                          className="action-btn rename"
                          title="Umbenennen"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteAudio(audio.name)}
                          className="action-btn delete"
                          title="Löschen"
                          data-testid={`delete-audio-${audio.name}`}
                        >
                          🗑️
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {filteredAudios.length === 0 && audios.length > 0 && (
                <div className="empty-state">
                  <p>Keine Musik gefunden für "{searchQuery}"</p>
                </div>
              )}
              {audios.length === 0 && (
                <div className="empty-state">
                  <div className="empty-icon">🎶</div>
                  <p>Keine Musik vorhanden</p>
                  <p className="empty-hint">Laden Sie Ihre erste MP3 hoch</p>
                </div>
              )}
            </div>

            {previewAudio && (
              <div className="preview-modal" onClick={() => setPreviewAudio(null)}>
                <div className="preview-content audio-preview-content" onClick={(e) => e.stopPropagation()}>
                  <div className="audio-player-wrapper">
                    <div className="audio-icon">🎵</div>
                    <audio
                      src={previewAudio}
                      controls
                      autoPlay
                      className="audio-preview"
                      data-testid="audio-preview"
                    />
                  </div>
                  <button onClick={() => setPreviewAudio(null)} className="close-preview">Schließen</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MediaManagement;
